// Client side of the Share Widget Auto Access contract (security model v3, with keys). The
// server's api/helpers/UpdateToken.js is the source of truth; keep the signed message, the
// settings reads and the error texts in step with it.

const MIN_SIGNING_SECRET_LENGTH = 16;
export const TOKEN_TOO_SHORT = `Token / Salt must be at least ${MIN_SIGNING_SECRET_LENGTH} characters`;

/**
 * The two signed ways in, each with its own settings: Access/Update via Email in URL, and
 * Join/Update Via URL.
 */
export type AccessMethod = 'email' | 'join';
/** Every tab of the page: the signed methods, and SSO, which has a switch and no sign option. */
export type TabMethod = AccessMethod | 'sso';

/**
 * What the end of a /share/pci or /share/pci-join link names: an email, or (Request Sign only)
 * the person's Platform Unique ID.
 */
export type KeyKind = 'email' | 'platformId';

/** The parameter an ID key's link names its influencer type in, which the signature covers. */
export const INFLUENCER_TYPE_PARAM = 'influencer_type';

/**
 * How a link proves itself: not at all, with the method's Token / Salt itself, signed with it, or
 * (Access/Update via Email in URL only, for old integrations) as md5(email + Token / Salt).
 */
export type SignMode = 'none' | 'global_token' | 'request_sign' | 'legacy_md5';
/** The sign modes each method has. The Join method never had md5 links. */
const SIGN_MODES: Record<AccessMethod, SignMode[]> = {
  email: ['none', 'global_token', 'request_sign', 'legacy_md5'],
  join: ['none', 'global_token', 'request_sign']
};
/** What an absent or unknown sign mode means: the most secure. */
const DEFAULT_SIGN_MODE: SignMode = 'request_sign';

/** Each method's widget_settings keys. */
export const ACCESS_SETTINGS: Record<AccessMethod, {
  enabled: string;
  signMode: string;
  securityToken: string;
  globalTokenEdit: string;
}> = {
  email: {
    enabled: 'share_widget_access_via_email_enabled',
    signMode: 'share_widget_access_via_email_sign_mode',
    securityToken: 'share_widget_access_via_email_security_token',
    globalTokenEdit: 'share_widget_access_via_email_global_token_edit'
  },
  join: {
    enabled: 'share_widget_access_join_url_enabled',
    signMode: 'share_widget_access_join_url_sign_mode',
    securityToken: 'share_widget_access_join_url_security_token',
    globalTokenEdit: 'share_widget_access_join_url_global_token_edit'
  }
};
/** The SSO tab's one setting, its switch: absent means off. */
export const SSO_ENABLED_SETTING = 'share_widget_access_sso_enabled';
/** Whether Legacy Sign is available: the email method only. The server alone changes it. */
const LEGACY_MD5_SETTING = 'share_widget_access_legacy_md5_token';

export interface MethodAccess {
  enabled: boolean;
  signMode: SignMode;
  /** The Token / Salt exactly as saved, or null when blank. */
  securityToken: string | null;
  globalTokenEdit: boolean;
  /** Whether Legacy Sign is available; always false for the Join method. */
  legacyMd5: boolean;
}

/** The code that builds a link (node, php) and the code that POSTs it (nodePost, phpPost). */
export interface SigningSnippets {
  node: string;
  php: string;
  nodePost: string;
  phpPost: string;
}

export const NO_SNIPPETS: SigningSnippets = {node: '', php: '', nodePost: '', phpPost: ''};

export function normalizeEmail(email: unknown): string {
  return String(email ?? '').trim().toLowerCase();
}

/**
 * The key as it is signed: an email trimmed and lowercased; a Platform Unique ID trimmed with its
 * case kept, then '|' and the influencer type the link names (an ID is one person's only within a
 * type).
 */
export function signedKey(key: unknown, kind: KeyKind = 'email', type: unknown = ''): string {
  return kind === 'platformId' ? `${String(key ?? '').trim()}|${String(type ?? '').trim()}` : normalizeEmail(key);
}

/** hex(HMAC-SHA256(securityToken, signedKey(key) + '|' + projectCode + '|' + expires)) */
export async function emailAccessToken(
  securityToken: string,
  key: string,
  projectCode: string,
  expires: number | '' = '',
  kind: KeyKind = 'email',
  type = ''
): Promise<string> {
  const encoder = new TextEncoder();
  const cryptoKey = await crypto.subtle.importKey(
    'raw',
    encoder.encode(String(securityToken)),
    {name: 'HMAC', hash: 'SHA-256'},
    false,
    ['sign']
  );
  const message = `${signedKey(key, kind, type)}|${projectCode}|${expires ?? ''}`;
  return toHex(await crypto.subtle.sign('HMAC', cryptoKey, encoder.encode(message)));
}

/** 32 random bytes as 64 hex characters. */
export function generateSecurityToken(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return toHex(bytes.buffer);
}

/** Unix seconds `days` from now, for a sample link that expires. */
export function expiresInDays(days: number, now = Date.now()): number {
  return Math.floor(now / 1000) + days * 24 * 60 * 60;
}

export function parseWidgetSettings(settings: any): Record<string, any> | null {
  if (!settings) return {};
  if (typeof settings !== 'string') {
    return typeof settings === 'object' && !Array.isArray(settings)
      ? {...settings}
      : null;
  }
  try {
    const parsed = JSON.parse(settings);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : null;
  } catch (error) {
    return null;
  }
}

/**
 * One method's access settings, read the way the server's accessOf reads them: the method is off
 * until enabled, an absent or unknown sign mode (Legacy Sign included, on the Join method) is
 * Request Sign, the booleans go through toBoolean, and Legacy Sign (email only) counts as
 * available until a project's setting turns it off.
 */
export function accessOf(widgetSettings: Record<string, any> | null, method: AccessMethod): MethodAccess {
  const settings = widgetSettings && typeof widgetSettings === 'object' ? widgetSettings : {};
  const keys = ACCESS_SETTINGS[method];
  const securityToken = settings[keys.securityToken];
  const signMode = settings[keys.signMode];
  const legacyMd5 = settings[LEGACY_MD5_SETTING];
  return {
    enabled: toBoolean(settings[keys.enabled]),
    signMode: SIGN_MODES[method].includes(signMode) ? signMode : DEFAULT_SIGN_MODE,
    securityToken: typeof securityToken === 'string' && securityToken.trim() ? securityToken : null,
    globalTokenEdit: toBoolean(settings[keys.globalTokenEdit]),
    legacyMd5: method === 'email' && (legacyMd5 === undefined || legacyMd5 === null ? true : toBoolean(legacyMd5))
  };
}

/** Whether a tab's switch is on, read as the server reads it: SSO's own key, or the method's. */
export function isEnabled(widgetSettings: Record<string, any> | null, method: TabMethod): boolean {
  if (method !== 'sso') return accessOf(widgetSettings, method).enabled;
  return toBoolean(widgetSettings && typeof widgetSettings === 'object' ? widgetSettings[SSO_ENABLED_SETTING] : null);
}

/** The widget_settings key a tab's switch saves to. */
export function enabledSetting(method: TabMethod): string {
  return method === 'sso' ? SSO_ENABLED_SETTING : ACCESS_SETTINGS[method].enabled;
}

/**
 * Whether Legacy Sign is offered: on the email method, while it is still available and a token is
 * saved for old md5 links to have been made with, or while it is the mode in use. Once the server
 * turns it off it never comes back.
 */
export function legacySignAvailable(access: MethodAccess): boolean {
  return access.signMode === 'legacy_md5' || (!!access.securityToken && access.legacyMd5);
}

/**
 * Why the server would refuse this Token / Salt, or null: one newly set or changed must be at
 * least 16 characters once trimmed; the one already saved stays valid whatever its length.
 */
export function tokenProblem(token: string, savedToken: string | null): string | null {
  const next = String(token ?? '');
  if (next === (savedToken ?? '') || !next.trim()) return null;
  return next.trim().length < MIN_SIGNING_SECRET_LENGTH ? TOKEN_TOO_SHORT : null;
}

/** What a tab's code is built from. */
export interface LinkSnippetInputs {
  method: AccessMethod;
  /** The link up to its key: /share/pci/PROJECT/ or /share/pci-join/PROJECT/, with the campaign code when it has one. */
  prefix: string;
  keyKind: KeyKind;
  /** The sample key the code starts from. */
  sampleKey: string;
  /**
   * The ticked fields and targeting, as URL parameter and sample value, in table order; an ID key's
   * link names its influencer type (influencer_type) among them, which the signature covers.
   */
  fields: Array<[string, string]>;
  signMode: SignMode;
  includeExpiry: boolean;
  projectCode: string;
}

/**
 * The code for a link in the sign mode it needs, for a GET link (node, php) and for a POST from
 * the customer's server (nodePost, phpPost): none, the plain link; global_token, with the Token /
 * Salt itself; request_sign, signed for the key (and, with includeExpiry, expiring in 30 days;
 * without it the code has no expiry at all); legacy_md5, with md5(email + salt). A link without
 * fields is written as before, one string; with fields, a URL the fields are set on. A Platform
 * Unique ID key exists under Request Sign only, so in any other mode there is no code for it.
 */
export function linkSnippets(inputs: LinkSnippetInputs): SigningSnippets {
  const {prefix, keyKind, fields, signMode} = inputs;
  if (keyKind === 'platformId' && signMode !== 'request_sign') return NO_SNIPPETS;
  const expiring = signMode === 'request_sign' && inputs.includeExpiry;
  const key = keyKind === 'platformId' ? 'key' : 'email';
  const node = nodeLines(inputs, expiring);
  const php = phpLines(inputs, expiring);
  // What the link carries besides its key, as each language names the token, the expiry and an ID
  // key's influencer type (the variable its signature is made with).
  const params = (token: string, expires: string, type: string): Array<[string, string]> => [
    ...fields.map(([name, value]): [string, string] =>
      [name, keyKind === 'platformId' && name === INFLUENCER_TYPE_PARAM ? type : quote(value)]),
    ...(signMode === 'none' ? [] : [['option_update_token', token] as [string, string]]),
    ...(expiring ? [['expires', expires] as [string, string]] : [])
  ];
  const nodeParams = params(signMode === 'global_token' ? 'securityToken' : 'token', 'String(expires)', 'influencerType');
  const phpParams = params(signMode === 'global_token' ? '$securityToken' : '$token', '$expires', '$influencerType');
  const phpArray = `[${phpParams.map(([name, value]) => `${quote(name)} => ${value}`).join(', ')}]`;
  return {
    node: [
      ...node,
      '',
      ...(fields.length
        ? [
          `const url = new URL(${quote(prefix)} + encodeURIComponent(${key}));`,
          ...nodeParams.map(([name, value]) => `url.searchParams.set(${quote(name)}, ${value});`)
        ]
        : [plainNodeUrl(prefix, key, signMode, expiring)])
    ].join('\n'),
    php: [
      ...php,
      '',
      fields.length
        ? `$query = ${phpArray};\n$url = ${quote(prefix)} . rawurlencode($${key}) . '?' . http_build_query($query);`
        : plainPhpUrl(prefix, key, signMode, expiring)
    ].join('\n'),
    nodePost: [
      ...node,
      '',
      `fetch(${quote(prefix)} + encodeURIComponent(${key}), {`,
      '  method: \'POST\',',
      '  headers: {\'Content-Type\': \'application/json\', Accept: \'application/json\'},',
      `  body: JSON.stringify({${nodeParams.map(([name, value]) =>
        `${name}: ${name === 'expires' ? 'expires' : value}`).join(', ')}})`,
      '})',
      '  .then((response) => response.json())',
      '  .then((answer) => console.log(answer.shareUrl)); // the person\'s share page'
    ].join('\n'),
    phpPost: [
      ...php,
      '',
      `$fields = ${phpArray};`,
      `$curl = curl_init(${quote(prefix)} . rawurlencode($${key}));`,
      'curl_setopt_array($curl, [',
      '    CURLOPT_POST => true,',
      '    CURLOPT_POSTFIELDS => http_build_query($fields),',
      '    CURLOPT_HTTPHEADER => [\'Accept: application/json\'],',
      '    CURLOPT_RETURNTRANSFER => true',
      ']);',
      '$answer = json_decode(curl_exec($curl), true);',
      'curl_close($curl);',
      '$shareUrl = $answer[\'shareUrl\']; // the person\'s share page'
    ].join('\n')
  };
}

// A link without fields, as one string: the key, then the token and the expiry.
function plainNodeUrl(prefix: string, key: string, signMode: SignMode, expiring: boolean): string {
  if (signMode === 'none') return `const url = ${quote(prefix)} + encodeURIComponent(${key});`;
  const token = signMode === 'global_token' ? 'encodeURIComponent(securityToken)' : 'token';
  return `const url = ${quote(prefix)} + encodeURIComponent(${key})\n`
    + `  + '?option_update_token=' + ${token}${expiring ? ' + \'&expires=\' + expires' : ''};`;
}

function plainPhpUrl(prefix: string, key: string, signMode: SignMode, expiring: boolean): string {
  if (signMode === 'none') return `$url = ${quote(prefix)} . rawurlencode($${key});`;
  const token = signMode === 'global_token' ? 'rawurlencode($securityToken)' : '$token';
  return `$url = ${quote(prefix)} . rawurlencode($${key})\n`
    + `    . '?option_update_token=' . ${token}${expiring ? ' . \'&expires=\' . $expires' : ''};`;
}

// What comes before the URL: the token when the mode uses one, read from an environment variable
// named for the method and the field; the key; for Request Sign the signature, with the project
// code written into the signed message itself rather than held in a variable; for Legacy Sign
// md5(email + salt), the email exactly as the contact's is stored.
function nodeLines(inputs: LinkSnippetInputs, expiring: boolean): string[] {
  const {method, keyKind, signMode, projectCode} = inputs;
  const hashed = signMode === 'request_sign' || signMode === 'legacy_md5';
  const signed = keyKind === 'platformId' ? '${key.trim()}|${influencerType.trim()}' : '${email.trim().toLowerCase()}';
  return [
    ...(hashed ? ['const crypto = require(\'crypto\');', ''] : []),
    ...(signMode === 'none' ? [] : [
      `const securityToken = process.env.${secretVariable(method, signMode)}; // the ${secretName(signMode)}, exactly as saved`
    ]),
    keyLine('const ', ';', inputs),
    ...typeLine('const ', ';', inputs),
    ...(expiring ? ['const expires = Math.floor(Date.now() / 1000) + 30 * 24 * 60 * 60; // 30 days from now'] : []),
    ...(signMode === 'request_sign' ? [
      '',
      'const token = crypto.createHmac(\'sha256\', securityToken)',
      `  .update(\`${signed}|${templateText(projectCode)}|${expiring ? '${expires}' : ''}\`)`,
      '  .digest(\'hex\');'
    ] : []),
    ...(signMode === 'legacy_md5' ? [
      '',
      'const token = crypto.createHash(\'md5\').update(email + securityToken).digest(\'hex\');'
    ] : [])
  ];
}

function phpLines(inputs: LinkSnippetInputs, expiring: boolean): string[] {
  const {method, keyKind, signMode, projectCode} = inputs;
  const signed = keyKind === 'platformId' ? 'trim($key) . \'|\' . trim($influencerType)' : 'mb_strtolower(trim($email))';
  return [
    ...(signMode === 'none' ? [] : [
      `$securityToken = getenv('${secretVariable(method, signMode)}'); // the ${secretName(signMode)}, exactly as saved`
    ]),
    keyLine('$', ';', inputs),
    ...typeLine('$', ';', inputs),
    ...(expiring ? ['$expires = time() + 30 * 24 * 60 * 60; // 30 days from now'] : []),
    ...(signMode === 'request_sign' ? [
      '',
      `$token = hash_hmac('sha256', ${signed} . ${quote(`|${projectCode}|`)}${expiring ? ' . $expires' : ''}, $securityToken);`
    ] : []),
    ...(signMode === 'legacy_md5' ? ['', '$token = md5($email . $securityToken);'] : [])
  ];
}

// The sample key, in the variable the code signs and builds the link with.
function keyLine(declare: string, end: string, {keyKind, sampleKey, signMode}: LinkSnippetInputs): string {
  if (keyKind === 'platformId') {
    return `${declare}key = ${quote(sampleKey)}${end} // the person's Platform Unique ID, exactly as your system writes it`;
  }
  return `${declare}email = ${quote(sampleKey)}${end}${signMode === 'legacy_md5' ? ' // exactly as the contact\'s email is stored' : ''}`;
}

// An ID key's influencer type, as the link names it: the signature covers it.
function typeLine(declare: string, end: string, {keyKind, fields}: LinkSnippetInputs): string[] {
  if (keyKind !== 'platformId') return [];
  const type = fields.find(([name]) => name === INFLUENCER_TYPE_PARAM)?.[1] ?? '';
  return [`${declare}influencerType = ${quote(type)}${end} // the person's influencer type, which the ID is signed with`];
}

/**
 * The link's query for a sign mode: nothing, the Token / Salt itself, md5(email + Token / Salt)
 * (with the email exactly as stored), or the signature for the key (+ expiry). A Platform Unique
 * ID key exists under Request Sign only, so in any other mode it gets nothing; nor without the
 * influencer type its link names, which its signature covers.
 */
export async function accessQuery(
  signMode: SignMode,
  securityToken: string,
  key: string,
  projectCode: string,
  expires: number | '',
  kind: KeyKind = 'email',
  type = ''
): Promise<string> {
  if (signMode === 'none' || !securityToken) return '';
  if (kind === 'platformId' && (signMode !== 'request_sign' || !String(type).trim())) return '';
  if (signMode === 'global_token') return `option_update_token=${encodeURIComponent(securityToken)}`;
  if (signMode === 'legacy_md5') return `option_update_token=${md5Hex(`${key}${securityToken}`)}`;
  const token = await emailAccessToken(securityToken, key, projectCode, expires, kind, type);
  return `option_update_token=${token}${expires ? `&expires=${expires}` : ''}`;
}

// What the field holds in a mode: the token itself for Global Token, a salt to sign with otherwise.
function secretName(signMode: SignMode): string {
  return signMode === 'global_token' ? 'token' : 'salt';
}

// The environment variable the code reads the field from: one per method, as each tab has its own
// Token / Salt, named as the field is in the mode (SNOBALL_JOIN_URL_SALT, SNOBALL_EMAIL_ACCESS_TOKEN).
function secretVariable(method: AccessMethod, signMode: SignMode): string {
  return `SNOBALL_${method === 'join' ? 'JOIN_URL' : 'EMAIL_ACCESS'}_${secretName(signMode).toUpperCase()}`;
}

// A single-quoted literal that JavaScript and PHP read the same way.
function quote(value: string): string {
  return `'${String(value).replace(/\\/g, '\\\\').replace(/'/g, '\\\'')}'`;
}

// Text inside a JavaScript template literal, read back exactly as given.
function templateText(value: string): string {
  return String(value).replace(/\\/g, '\\\\').replace(/`/g, '\\`').replace(/\$\{/g, '\\${');
}

function toBoolean(value: unknown): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['true', '1', 'yes', 'on'].includes(value.trim().toLowerCase());
  return false;
}

// MD5, which WebCrypto does not offer, for the Legacy Sign sample only.
const MD5_SHIFTS = [
  7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
  5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
  4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
  6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21
];
const MD5_CONSTANTS = Array.from({length: 64}, (_, index) => Math.floor(Math.abs(Math.sin(index + 1)) * 2 ** 32) | 0);

/** md5 hex of a string's UTF-8 bytes, as node's crypto.createHash('md5') and PHP's md5() give it. */
export function md5Hex(value: string): string {
  const bytes = new TextEncoder().encode(value);
  const length = bytes.length;
  const words = new Uint32Array((((length + 8) >>> 6) + 1) * 16);
  for (let i = 0; i < length; i++) words[i >> 2] |= bytes[i] << ((i % 4) * 8);
  words[length >> 2] |= 0x80 << ((length % 4) * 8);
  words[words.length - 2] = (length * 8) >>> 0;
  words[words.length - 1] = Math.floor(length / 0x20000000);
  let [a, b, c, d] = [0x67452301, 0xefcdab89 | 0, 0x98badcfe | 0, 0x10325476];
  for (let block = 0; block < words.length; block += 16) {
    const [startA, startB, startC, startD] = [a, b, c, d];
    for (let step = 0; step < 64; step++) {
      let mixed: number;
      let word: number;
      if (step < 16) {
        mixed = (b & c) | (~b & d);
        word = step;
      } else if (step < 32) {
        mixed = (d & b) | (~d & c);
        word = (5 * step + 1) % 16;
      } else if (step < 48) {
        mixed = b ^ c ^ d;
        word = (3 * step + 5) % 16;
      } else {
        mixed = c ^ (b | ~d);
        word = (7 * step) % 16;
      }
      const sum = (a + mixed + MD5_CONSTANTS[step] + words[block + word]) | 0;
      const shift = MD5_SHIFTS[step];
      [a, d, c] = [d, c, b];
      b = (b + ((sum << shift) | (sum >>> (32 - shift)))) | 0;
    }
    a = (a + startA) | 0;
    b = (b + startB) | 0;
    c = (c + startC) | 0;
    d = (d + startD) | 0;
  }
  return [a, b, c, d]
    .map((part) => [0, 8, 16, 24].map((bits) => ((part >>> bits) & 0xff).toString(16).padStart(2, '0')).join(''))
    .join('');
}

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
